<script lang="ts">
import type { CalendarFeed } from '@codot/contracts';
import { mediaApi } from '../../api/content.api';
/** One request per link and length for every preview on the page (thumbnails, sections), kept 5 minutes. */
const cache = new Map<string, { at: number; p: Promise<CalendarFeed> }>();
function loadFeed(url: string, days: number): Promise<CalendarFeed> {
  const key = `${days}|${url}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < 5 * 60_000) return hit.p;
  const p = mediaApi.calendarPreview(url, days);
  cache.set(key, { at: Date.now(), p });
  p.catch(() => cache.delete(key));
  return p;
}
</script>

<script setup lang="ts">
/**
 * Calendar app preview, laid out like the player's (render/widgets.ts `calendar`): the same agenda rules
 * (contracts `agenda`), the real events of the link (read by the API), sized in container-query units.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { agenda, CalendarUrlSchema, type CalendarWidget } from '@codot/contracts';
import { errorMessage } from '../../api/http';

const props = withDefaults(defineProps<{ config: CalendarWidget; zone?: string; live?: boolean }>(), { zone: 'Asia/Dubai', live: true });
const { t } = useI18n();

const feed = ref<CalendarFeed | null>(null);
const loading = ref(false);
const error = ref('');
let debounce = 0;
watch(
  () => [props.config.icalUrl, props.config.daysAhead] as const,
  ([url, days]) => {
    clearTimeout(debounce);
    error.value = '';
    if (!CalendarUrlSchema.safeParse(url).success) return void (feed.value = null);
    debounce = window.setTimeout(async () => {
      loading.value = true;
      try {
        feed.value = await loadFeed(url, days);
      } catch (e) {
        feed.value = null;
        error.value = errorMessage(e);
      } finally {
        loading.value = false;
      }
    }, 500);
  },
  { immediate: true },
);
const now = ref(Date.now());
const timer = setInterval(() => props.live && (now.value = Date.now()), 60_000);
onBeforeUnmount(() => (clearInterval(timer), clearTimeout(debounce)));

const ar = computed(() => props.config.language === 'ar');
const L = computed(() =>
  ar.value
    ? { today: 'اليوم', tomorrow: 'غدًا', allDay: 'طوال اليوم', none: 'لا توجد أحداث قادمة', now: 'الآن' }
    : { today: t('appsCat.calToday'), tomorrow: t('appsCat.calTomorrow'), allDay: t('appsCat.calAllDay'), none: t('appsCat.calNone'), now: t('appsCat.calNow') },
);
const loc = computed(() => (ar.value ? 'ar-AE' : 'en-GB'));
const nu = computed(() => (ar.value ? 'arab' : 'latn'));
const fmtTime = computed(() => new Intl.DateTimeFormat(loc.value, { timeZone: props.zone, numberingSystem: nu.value, hour: '2-digit', minute: '2-digit', hour12: props.config.format === '12h' }));
const fmtDay = computed(() => new Intl.DateTimeFormat(loc.value, { timeZone: 'UTC', numberingSystem: nu.value, weekday: 'long', day: 'numeric', month: 'long' }));

const days = computed(() => (feed.value ? agenda(feed.value.events, now.value, props.zone, props.config.daysAhead, props.config.maxEvents) : []));
const title = computed(() => props.config.title || feed.value?.name || '');
const unit = computed(() => {
  const rows = days.value.reduce((n, d) => n + 1 + d.events.reduce((m, e) => m + (props.config.showLocation && e.location ? 1.6 : 1.15), 0), 0);
  return Math.min((1 - 0.1 - (title.value ? 0.13 : 0)) / Math.max(rows, 3) / 1.5, 0.075);
});
const dayLabel = (d: { offset: number; date: string }) => (d.offset === 0 ? L.value.today : d.offset === 1 ? L.value.tomorrow : fmtDay.value.format(Date.parse(`${d.date}T00:00:00Z`)));
const when = (e: { allDay: boolean; start: string; end: string }) => (e.allDay ? L.value.allDay : `${fmtTime.value.format(Date.parse(e.start))}–${fmtTime.value.format(Date.parse(e.end))}`);
const message = computed(() => (error.value ? error.value : loading.value && !feed.value ? t('appsCat.calLoading') : !feed.value ? t('appsCat.calPasteLink') : L.value.none));
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden [container-type:size]" :dir="ar ? 'rtl' : 'ltr'" :style="{ background: config.background, color: config.foreground, padding: 'min(5cqh, 4cqw)' }">
    <p v-if="title" class="truncate font-bold" :style="{ fontSize: 'min(8.5cqh, 6cqw)', marginBottom: '2.5cqh' }">{{ title }}</p>
    <div v-if="days.length" class="min-h-0 flex-1 overflow-hidden">
      <template v-for="d in days" :key="d.date">
        <p class="truncate font-bold" :style="{ color: config.accent, fontSize: `${unit * 100}cqh`, margin: `${unit * 30}cqh 0 ${unit * 15}cqh` }">{{ dayLabel(d) }}</p>
        <div v-for="(e, i) in d.events" :key="i" class="flex items-baseline" :style="{ gap: '0.8em', fontSize: `${unit * 92}cqh`, marginBottom: `${unit * 18}cqh` }">
          <span class="shrink-0 tabular-nums opacity-80" style="min-width: 7.5em">{{ when(e) }}</span>
          <div class="min-w-0 flex-1">
            <p class="truncate font-medium"><span v-if="e.now" class="font-bold" :style="{ color: config.accent }">{{ L.now }} · </span>{{ e.title }}</p>
            <p v-if="config.showLocation && e.location" class="truncate opacity-70" style="font-size: 0.75em">{{ e.location }}</p>
          </div>
        </div>
      </template>
    </div>
    <p v-else class="flex flex-1 items-center justify-center px-4 text-center opacity-75" :style="{ fontSize: 'min(6cqh, 4.5cqw)' }">{{ message }}</p>
  </div>
</template>
